import React, { Fragment, useRef, useState } from 'react'
import { Dialog, Transition } from '@headlessui/react'
import { DocumentArrowDownIcon, XMarkIcon } from '@heroicons/react/24/outline'
import { formatCurrency } from "../../utils/format.js"
import { toast } from 'react-toastify'
import html2pdf from 'html2pdf.js'
import useAuthStore from '../../stores/authStore.js'

const NotaFiscalViewer = ({ arquivoImagem }) => {
  if (!arquivoImagem) return null;

  return (
    <div className="mt-8">
      <img 
        src={`${import.meta.env.VITE_API_URL}/api/${arquivoImagem}`}
        alt="Nota Fiscal" 
        className="w-full h-auto object-contain rounded-lg shadow-sm" 
        onError={(e) => {
          console.error('Erro ao carregar nota fiscal:', e);
          e.target.style.display = 'none';
        }}
      />
    </div>
  );
};

const ReportContent = React.forwardRef(({ data }, ref) => {
  const { user } = useAuthStore();

  return (
    <div ref={ref} className="p-6 bg-white text-gray-900">
      {/* Logo e Título */}
      <div className="flex items-start gap-4 mb-6">
        <img 
          src={`${import.meta.env.VITE_API_URL}/api/${user?.avatarUrl}`}
          alt="Avatar" 
          crossOrigin="anonymous"
          className="h-16 w-16 object-contain bg-white rounded-lg p-1 border border-gray-200"
          onError={(e) => {
            console.error('Erro ao carregar avatar:', e);
            e.target.style.display = 'none';
          }}
        />
        <div>
          <h2 className="text-xl font-bold text-gray-900">{user?.trade_name}</h2>
          <p className="text-sm text-gray-500">Relatório de Retenção de Impostos</p>
        </div>
      </div>

      {/* Filtros Aplicados */}
      <div className="mb-6">
        <h3 className="text-sm font-medium text-gray-700">Filtros Aplicados</h3>
        <p className="text-sm text-gray-500">Status: {data.filtroStatus}</p>
      </div>

      {/* Resumo */}
      <div className="mb-6">
        <h3 className="text-sm font-medium text-gray-700 mb-4">Resumo</h3>
        <div className="grid grid-cols-4 gap-4">
          <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
            <p className="text-sm text-gray-500">Total em Notas</p>
            <p className="text-lg font-semibold text-gray-900">{formatCurrency(data.totalNotas)}</p>
          </div>
          <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
            <p className="text-sm text-gray-500">Total IRRF</p>
            <p className="text-lg font-semibold text-gray-900">{formatCurrency(data.totalIRRF)}</p>
          </div>
          <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
            <p className="text-sm text-gray-500">Total ISS</p>
            <p className="text-lg font-semibold text-gray-900">{formatCurrency(data.totalISS)}</p>
          </div>
          <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
            <p className="text-sm text-gray-500">Total Líquido</p>
            <p className="text-lg font-semibold text-gray-900">{formatCurrency(data.totalLiquido)}</p>
          </div>
        </div>
      </div>

      {/* Tabela de Notas Fiscais */}
      <div className="mb-8">
        <h3 className="text-sm font-medium text-gray-700 mb-4">Resumo das Notas Fiscais</h3>
        <div className="overflow-hidden rounded-lg border border-gray-200">
          <table className="min-w-full text-[10px] divide-y divide-gray-200 table-fixed">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="w-[15%] px-1 py-1 text-left text-[10px] font-medium text-gray-500 uppercase">Fonte Pagadora</th>
                <th scope="col" className="w-[25%] px-1 py-1 text-left text-[10px] font-medium text-gray-500 uppercase">Fornecedor</th>
                <th scope="col" className="w-[8%] px-1 py-1 text-left text-[10px] font-medium text-gray-500 uppercase whitespace-nowrap">Data</th>
                <th scope="col" className="w-[8%] px-1 py-1 text-left text-[10px] font-medium text-gray-500 uppercase whitespace-nowrap">Nº Nota</th>
                <th scope="col" className="w-[10%] px-1 py-1 text-right text-[10px] font-medium text-gray-500 uppercase whitespace-nowrap">Valor</th>
                <th scope="col" className="w-[8%] px-1 py-1 text-right text-[10px] font-medium text-gray-500 uppercase whitespace-nowrap">IRRF</th>
                <th scope="col" className="w-[8%] px-1 py-1 text-right text-[10px] font-medium text-gray-500 uppercase whitespace-nowrap">ISS</th>
                <th scope="col" className="w-[10%] px-1 py-1 text-right text-[10px] font-medium text-gray-500 uppercase whitespace-nowrap">Líquido</th>
                <th scope="col" className="w-[8%] px-1 py-1 text-center text-[10px] font-medium text-gray-500 uppercase whitespace-nowrap">Status</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {data.notasFiltradas.map((nota) => (
                <tr key={nota.id}>
                  <td className="w-[15%] px-1 py-1 text-[10px] text-gray-900 break-words">{nota.payerSource?.nomeFonte || '-'}</td>
                  <td className="w-[25%] px-1 py-1 text-[10px] text-gray-900 break-words">{nota.fornecedorNome}</td>
                  <td className="w-[8%] px-1 py-1 text-[10px] text-gray-900 whitespace-nowrap">{new Date(nota.dataEmissao).toLocaleDateString()}</td>
                  <td className="w-[8%] px-1 py-1 text-[10px] text-gray-900 whitespace-nowrap">{nota.numeroNota}</td>
                  <td className="w-[10%] px-1 py-1 text-[10px] text-gray-900 text-right whitespace-nowrap">{formatCurrency(nota.valorNota)}</td>
                  <td className="w-[8%] px-1 py-1 text-[10px] text-gray-900 text-right whitespace-nowrap">{formatCurrency(nota.valorRetencaoIRRF)}</td>
                  <td className="w-[8%] px-1 py-1 text-[10px] text-gray-900 text-right whitespace-nowrap">{formatCurrency(nota.valorRetencaoISS)}</td>
                  <td className="w-[10%] px-1 py-1 text-[10px] text-gray-900 text-right whitespace-nowrap">{formatCurrency(nota.liquidoApagar)}</td>
                  <td className="w-[8%] px-1 py-1 text-[10px] whitespace-nowrap text-center">
                    <span className={`px-1 py-0.5 inline-flex text-[10px] leading-4 font-semibold rounded-full ${
                      nota.status === 'RECEBIDO' 
                        ? 'bg-green-100 text-green-800'
                        : 'bg-yellow-100 text-yellow-800'
                    }`}>
                      {nota.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Imagens das Notas Fiscais */}
      <div className="space-y-8">
        <h3 className="text-sm font-medium text-gray-700 mb-4">Notas Fiscais</h3>
        {data.notasFiltradas.map((nota) => {
          console.log('Nota fiscal:', nota); // Debug
          return (
            <div key={nota.id} className="bg-gray-50 p-4 rounded-lg border border-gray-200">
              <div className="mb-2">
                <p className="font-medium text-gray-900">{nota.fornecedorNome}</p>
                <p className="text-sm text-gray-500">Nota Fiscal: {nota.numeroNota}</p>
                <p className="text-sm text-gray-500">Arquivo: {nota.arquivoImagem}</p>
              </div>
              <NotaFiscalViewer arquivoImagem={nota.arquivoImagem} />
            </div>
          );
        })}
      </div>
    </div>
  );
});

ReportContent.displayName = 'ReportContent';

const ReportModal = ({ isOpen, onClose, data }) => {
  const componentRef = useRef();

  const handleExport = async () => {
    const element = componentRef.current;
    
    // Mostra toast de carregamento
    const toastId = toast.loading('Gerando PDF...');

    try {
      // Configurar opções do html2pdf
      const opt = {
        margin: [10, 10],
        filename: 'relatorio-retencao-impostos.pdf',
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { 
          scale: 2,
          useCORS: true,
          allowTaint: true,
          logging: true,
          letterRendering: true,
        },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'landscape' }
      };

      await html2pdf().set(opt).from(element).save();

      toast.update(toastId, {
        render: 'Relatório exportado com sucesso!',
        type: 'success',
        isLoading: false,
        autoClose: 3000
      });
    } catch (error) {
      console.error('Erro ao exportar PDF:', error);
      toast.update(toastId, {
        render: 'Erro ao exportar relatório. Tente novamente.',
        type: 'error',
        isLoading: false,
        autoClose: 3000
      });
    }
  };

  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-slate-900 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-4xl border border-slate-800">
                {/* Header */}
                <div className="flex items-center justify-between border-b border-slate-700 px-6 py-4 bg-slate-800">
                  <h3 className="text-lg font-medium text-white">
                    Relatório de Retenção de Impostos
                  </h3>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={handleExport}
                      className="inline-flex items-center gap-x-1.5 rounded-md bg-green-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-green-500 focus:ring-2 focus:ring-offset-2 focus:ring-offset-slate-900 focus:ring-green-600"
                    >
                      <DocumentArrowDownIcon className="-ml-0.5 h-5 w-5" aria-hidden="true" />
                      Exportar
                    </button>
                    <button
                      type="button"
                      className="rounded-md bg-slate-800 text-slate-400 hover:text-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 focus:ring-offset-slate-900"
                      onClick={onClose}
                    >
                      <span className="sr-only">Fechar</span>
                      <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                    </button>
                  </div>
                </div>

                {/* Content */}
                <div className="px-6 py-4 max-h-[calc(100vh-200px)] overflow-y-auto bg-slate-900">
                  <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                     <ReportContent ref={componentRef} data={data} />
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default ReportModal;
